import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { BesharpTableComponent, BesharpTableHeaderDefinition } from '../../modules/besharp-table/component/besharp-table.component';
import { ProviderService } from '../../services/provider.service';
import { SessionHistoryItem } from '../../services/pending-sessions.service';
import { BehaviorSubject, debounceTime, distinctUntilChanged, tap } from 'rxjs';
import { AntiMemLeak } from '../../classes/anti-mem-leak';

interface SessionHistoryRow extends SessionHistoryItem {
  id: string;
  date: string;
}

@Component({
  selector: 'app-session-history',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    InputTextModule,
    ButtonModule,
    IconFieldModule,
    InputIconModule,
    ToastModule,
    BesharpTableComponent
  ],
  templateUrl: './session-history.component.html',
  styleUrl: './session-history.component.scss'
})
export class SessionHistoryComponent extends AntiMemLeak implements OnInit, OnDestroy {
  sessions: SessionHistoryRow[] = [];
  allSessions: SessionHistoryRow[] = [];
  totalCount = 0;
  loading = false;
  searchFilter = '';

  debounceSubject = new BehaviorSubject<string>('');

  tableDefinitions: BesharpTableHeaderDefinition[] = [
    {
      def: 'id',
      title: '#',
      sortable: true,
      columnFilter: false,
    },
    {
      def: 'serialNumber',
      title: 'Serial Number',
      sortable: true,
      columnFilter: false,
    },
    {
      def: 'note',
      title: 'Note',
      sortable: false,
      columnFilter: false,
    },
    {
      def: 'startDate',
      title: 'Start Time',
      sortable: true,
      columnFilter: false,
    },
    {
      def: 'endDate',
      title: 'End Time',
      sortable: true,
      columnFilter: false,
    }
  ];

  constructor(
    private providerService: ProviderService,
    private messageService: MessageService
  ) {
    super();
    this.subscriptions.add(
      this.debounceSubject
        .pipe(
          debounceTime(500),
          distinctUntilChanged(),
          tap((value) => this.onSearch(value)),
        )
        .subscribe(),
    );
  }

  ngOnInit() {
    this.loadHistory();
  }

  override ngOnDestroy() {
    super.ngOnDestroy();
  }

  loadHistory() {
    this.loading = true;
    this.subscriptions.add(this.providerService.pendingSessionsService.getSessionHistory().subscribe({
      next: (items) => {
        this.allSessions = items.map((item, idx) => ({
          ...item,
          id: (idx + 1).toString(),
          date: new Date(item.timestamp).toLocaleString(),
          startDate: new Date(item.startTime).toLocaleString(),
          endDate: new Date(item.endTime).toLocaleString()
        }));
        this.sessions = [...this.allSessions];
        this.totalCount = this.allSessions.length;
        this.loading = false;

        if (this.searchFilter.trim()) {
          this.onSearch(this.searchFilter);
        }
      },
      error: (error) => {
        this.loading = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load session history: ' + (error?.message || 'Unknown error'),
          life: 3000
        });
      }
    }));
  }

  onSearch(searchTerm: string): void {
    if (searchTerm.trim()) {
      const lowerSearchTerm = searchTerm.toLowerCase();
      this.sessions = this.allSessions.filter(session =>
        session.serialNumber.toLowerCase().includes(lowerSearchTerm) ||
        session.note.toLowerCase().includes(lowerSearchTerm) ||
        session.date.toLowerCase().includes(lowerSearchTerm)
      );
    } else {
      this.sessions = [...this.allSessions];
    }
  }

  sendFilterQuery(event: any): void {
    this.searchFilter = event.target.value;
    this.debounceSubject.next(this.searchFilter);
  }

  onRowClicked(row: SessionHistoryRow): void {
    console.log('Row clicked:', row);
  }
}
